const CATALOG_ENDPOINT = '/api/watchvim-json?type=catalog';
const WATCHVIM_BLOB_HOST = 't6ht6kdwnezp05ut.public.blob.vercel-storage.com';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function first(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function firstUrl(...values) {
  for (const value of values) {
    if (!value) continue;
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'object') {
      const nested = first(value.url, value.src, value.href, value.publicUrl, value.public_url);
      if (typeof nested === 'string' && nested.trim()) return nested.trim();
    }
  }
  return '';
}

function normalizeMediaUrl(value) {
  const raw = firstUrl(value);
  if (!raw) return '';
  const candidate = raw.startsWith('//') ? `https:${raw}` : raw;
  try {
    const url = new URL(candidate, 'https://watchvim.com');
    if (url.hostname === WATCHVIM_BLOB_HOST) {
      return `/media/blob${url.pathname}${url.search}`;
    }
  } catch (_) {}
  if (/^(data:|blob:|https?:\/\/)/i.test(candidate)) return candidate;
  if (candidate.startsWith('/')) return candidate;
  return candidate;
}

function posterUrl(raw) {
  return normalizeMediaUrl(firstUrl(
    raw?.posterUrl, raw?.poster_url,
    raw?.appImages?.tvPosterUrl, raw?.appImages?.mobilePosterUrl,
    raw?.appImages?.posterUrl, raw?.appImages?.poster,
    raw?.images?.poster, raw?.images?.posterUrl, raw?.images?.poster_url,
    raw?.poster, raw?.cover, raw?.coverUrl, raw?.cover_url,
    raw?.thumbnailUrl, raw?.thumbnail_url, raw?.thumbnail, raw?.image, raw?.artwork,
  ));
}

function heroUrl(raw) {
  return normalizeMediaUrl(firstUrl(
    raw?.featuredHeroUrl, raw?.featured_hero_url,
    raw?.featureHeroImageUrl, raw?.featureImageUrl,
    raw?.heroUrl, raw?.hero_url, raw?.backdropUrl, raw?.backdrop_url,
    raw?.imageUrl, raw?.image_url, raw?.slideImageUrl, raw?.slide_image_url,
    raw?.appImages?.tvHeroUrl, raw?.appImages?.mobileHeroUrl,
    raw?.appImages?.heroUrl, raw?.appImages?.hero,
    raw?.images?.hero, raw?.images?.heroUrl, raw?.images?.hero_url,
    raw?.images?.backdrop, raw?.images?.backdropUrl,
    raw?.heroImage, raw?.hero_image, raw?.hero, raw?.backdrop,
    raw?.landscapeImage, raw?.landscape_image, raw?.landscapeUrl, raw?.landscape_url,
    raw?.thumbnailUrl, raw?.thumbnail_url, raw?.thumbnail,
    posterUrl(raw),
  ));
}

function mobileHeroUrl(raw) {
  return normalizeMediaUrl(firstUrl(
    raw?.images?.mobileHero, raw?.images?.mobileHeroUrl,
    raw?.mobileHeroUrl, raw?.mobile_hero_url,
    raw?.appImages?.mobileHeroUrl,
    heroUrl(raw), posterUrl(raw),
  ));
}

function titleLogoUrl(raw) {
  return normalizeMediaUrl(firstUrl(
    raw?.titleLogoUrl, raw?.title_logo_url,
    raw?.images?.titleLogoUrl, raw?.images?.title_logo_url, raw?.images?.titleLogo,
    raw?.logoUrl, raw?.logo_url, raw?.logo,
  ));
}

function episodeImageUrl(raw) {
  return normalizeMediaUrl(firstUrl(
    raw?.thumbnailUrl, raw?.thumbnail_url, raw?.thumbUrl, raw?.thumb_url,
    raw?.thumbnail, raw?.thumb, raw?.image,
    raw?.posterUrl, raw?.poster_url, raw?.poster, raw?.artwork,
    raw?.landscapeImage, raw?.landscape_image,
    raw?.appImages?.mobileHeroUrl, raw?.appImages?.heroUrl,
  ));
}

function slugify(value = '') {
  return String(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function normalizeGenres(raw) {
  const value = first(raw.genres, raw.genre, []);
  const values = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(/[,|]/) : []);
  return values.map((genre) => typeof genre === 'string' ? genre.trim() : String(first(genre?.name, genre?.title, '')).trim()).filter(Boolean);
}

function normalizeEpisode(raw, seriesId, fallbackIndex = 0, seasonFallback = 1) {
  const season = Number(first(raw.season, raw.seasonNumber, raw.season_number, seasonFallback)) || seasonFallback || 1;
  const episode = Number(first(raw.episode, raw.episodeNumber, raw.episode_number, fallbackIndex + 1)) || fallbackIndex + 1;
  return {
    id: String(first(raw.id, raw.episodeId, raw.episode_id, `${seriesId}-s${season}e${episode}`)),
    seriesId,
    season,
    episode,
    title: String(first(raw.title, raw.name, `Episode ${episode}`)),
    description: String(first(raw.description, raw.summary, raw.synopsis) || ''),
    image: episodeImageUrl(raw),
    duration: first(raw.duration, raw.runtime, raw.runtimeMinutes, raw.runtime_minutes, raw.runtimeMins),
    published: first(raw.isPublished, raw.published, true) !== false,
    raw,
  };
}

function extractEpisodes(raw, seriesId) {
  if (Array.isArray(raw.episodes)) {
    return raw.episodes.map((episode, index) => normalizeEpisode(episode, seriesId, index)).filter((episode) => episode.published);
  }
  return asArray(raw.seasons).flatMap((seasonRaw, seasonIndex) => {
    const seasonNumber = Number(first(seasonRaw.seasonNumber, seasonRaw.season_number, seasonRaw.number, seasonRaw.season, seasonIndex + 1)) || seasonIndex + 1;
    return asArray(seasonRaw.episodes)
      .map((episode, episodeIndex) => normalizeEpisode(episode, seriesId, episodeIndex, seasonNumber))
      .filter((episode) => episode.published);
  });
}

function normalizeTitle(raw, fallbackIndex = 0) {
  const name = String(first(raw.title, raw.name, raw.seriesTitle, raw.series_title, `Untitled ${fallbackIndex + 1}`));
  const kindRaw = String(first(raw.kind, raw.type, raw.contentType, raw.content_type, raw.format, '')).toLowerCase();
  const listingCategory = String(first(raw.listingCategory, raw.listing_category, '')).trim().toLowerCase();
  const shortsCategory = String(first(raw.shortsCategory, raw.shorts_category, '')).trim().toLowerCase();
  const hasEpisodes = asArray(raw.episodes).length > 0 || asArray(raw.seasons).some((season) => asArray(season?.episodes).length > 0);
  const isSeries = hasEpisodes || shortsCategory === 'series' || /series|show|episode/.test(kindRaw);
  const isShort = Boolean(first(raw.short, raw.isShort, raw.is_short, false)) || listingCategory === 'shorts' || /short/.test(kindRaw);
  const id = String(first(raw.id, raw.titleId, raw.title_id, raw.seriesId, raw.series_id, raw.slug, slugify(name)));
  return {
    id,
    slug: String(first(raw.slug, slugify(name))),
    title: name,
    titleLogo: titleLogoUrl(raw),
    kind: isSeries ? 'series' : 'movie',
    listingCategory: listingCategory || (isShort ? 'shorts' : (isSeries ? 'series' : 'films')),
    shortsCategory: isShort ? (shortsCategory || (isSeries ? 'series' : 'films')) : '',
    description: String(first(raw.description, raw.summary, raw.synopsis, raw.logline) || ''),
    year: first(raw.year, raw.releaseYear, raw.release_year),
    duration: first(raw.duration, raw.runtime, raw.runtimeMinutes, raw.runtime_minutes, raw.runtimeMins),
    genres: normalizeGenres(raw),
    image: posterUrl(raw),
    backdrop: heroUrl(raw),
    mobileBackdrop: mobileHeroUrl(raw),
    featured: Boolean(first(raw.featured, raw.isFeatured, raw.is_featured, false)),
    original: Boolean(first(raw.original, raw.isOriginal, raw.is_original, raw.watchvimOriginal, raw.watchvim_original, false)),
    short: isShort,
    published: first(raw.isPublished, raw.published, true) !== false,
    episodes: isSeries ? extractEpisodes(raw, id) : [],
    raw,
  };
}

function candidateRailList(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  for (const candidate of [payload.rails, payload.rows, payload.groups, payload.catalog, payload.data]) {
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
}

function extractRawRails(payload) {
  return candidateRailList(payload)
    .filter((entry) => entry && Array.isArray(entry.items) && entry.items.length)
    .map((entry, index) => ({
      id: String(first(entry.id, entry.slug, slugify(first(entry.title, entry.name, `rail-${index + 1}`)))),
      title: String(first(entry.title, entry.name, `Featured ${index + 1}`)),
      items: entry.items,
    }));
}

function flattenGroups(list) {
  if (!Array.isArray(list)) return [];
  const grouped = list.some((entry) => Array.isArray(entry?.items));
  return grouped ? list.flatMap((entry) => asArray(entry?.items)) : list;
}

function extractTitles(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return flattenGroups(payload);
  if (typeof payload !== 'object') return [];

  // CMS contract wins over legacy grouped rails. The CMS already filters this array to published titles.
  if (Array.isArray(payload.titles)) return payload.titles;

  const rawRails = extractRawRails(payload);
  if (rawRails.length) return rawRails.flatMap((rail) => rail.items);

  const candidates = [payload.catalog, payload.items, payload.content, payload.results, payload.data, payload.groups, payload.rails, payload.rows];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return flattenGroups(candidate);
    if (candidate && typeof candidate === 'object') {
      const nested = first(candidate.titles, candidate.items, candidate.results, candidate.groups, candidate.rails, candidate.rows);
      if (Array.isArray(nested)) return flattenGroups(nested);
    }
  }
  return ['featured', 'movies', 'series', 'shows', 'originals', 'shorts'].flatMap((key) => asArray(payload[key]));
}

function mergeTitle(existing, next) {
  if (!existing) return next;
  const episodes = next.episodes.length > existing.episodes.length ? next.episodes : existing.episodes;
  return {
    ...existing,
    titleLogo: existing.titleLogo || next.titleLogo,
    listingCategory: existing.listingCategory || next.listingCategory,
    shortsCategory: existing.shortsCategory || next.shortsCategory,
    description: existing.description || next.description,
    image: existing.image || next.image,
    backdrop: existing.backdrop || next.backdrop,
    genres: existing.genres.length ? existing.genres : next.genres,
    episodes,
    featured: existing.featured || next.featured,
    original: existing.original || next.original,
    short: existing.short || next.short,
  };
}

function dedupeNormalized(items) {
  const deduped = new Map();
  items.filter((item) => item?.published !== false).forEach((item) => deduped.set(item.id, mergeTitle(deduped.get(item.id), item)));
  return [...deduped.values()];
}

function idSet(value) {
  return new Set(asArray(value).map((id) => String(id)).filter(Boolean));
}

function normalizeHeroEntry(raw, canonicalById, index = 0) {
  if (!raw) return null;
  const titleId = String(first(raw.titleId, raw.title_id, raw.contentId, raw.content_id, raw.id, ''));
  const source = titleId ? canonicalById.get(titleId) : null;
  if (titleId && !source) return null; // Never promote an unpublished/missing CMS title.

  // The CMS resolves every asset mode at publication. Preserve that snapshot,
  // including deliberately empty fields; clients must not reselect canonical assets.
  if (raw.assetsResolved === true && source) {
    return {
      ...source,
      title: String(raw.title || source.title),
      description: String(raw.synopsis || ''),
      titleLogo: normalizeMediaUrl(String(raw.titleLogoUrl || '')),
      image: normalizeMediaUrl(String(raw.posterUrl || '')),
      backdrop: normalizeMediaUrl(String(raw.heroUrl || raw.posterUrl || '')),
      mobileBackdrop: mobileHeroUrl(raw),
      promotion: { titleId, raw },
    };
  }

  // Compatibility for older, unresolved linked entries only.
  if (raw.assetMode === 'title' && source) {
    return { ...source, promotion: { titleId, raw: {
      ...source.raw, titleId, assetMode: 'title', assetsResolved: true,
      trailerPlaybackId: String(source.raw?.trailerPlaybackId || ''),
    } } };
  }

  const title = String(first(raw.title, raw.name, source?.title, `Featured ${index + 1}`));
  const fallback = source || normalizeTitle(raw, index);
  return {
    ...fallback,
    id: source?.id || fallback.id,
    title,
    titleLogo: titleLogoUrl(raw) || source?.titleLogo || fallback.titleLogo || '',
    description: String(first(raw.description, raw.summary, raw.synopsis, source?.description, fallback.description) || ''),
    image: posterUrl(raw) || source?.image || fallback.image,
    backdrop: heroUrl(raw) || source?.backdrop || source?.image || fallback.backdrop || fallback.image,
    mobileBackdrop: mobileHeroUrl(raw) || source?.mobileBackdrop || source?.backdrop || fallback.image,
    promotion: { titleId: titleId || source?.id || fallback.id, raw },
  };
}

function normalizeHeroList(value, canonicalById) {
  const list = Array.isArray(value) ? value : asArray(value?.hero || value?.slides);
  return list.map((entry, index) => normalizeHeroEntry(entry, canonicalById, index)).filter(Boolean);
}

function normalizeProgramming(payload, canonicalById, featuredIds, originalIds) {
  const home = payload && typeof payload === 'object' ? (payload.home || {}) : {};
  const sectionHeroes = home.sectionHeroes || {};
  const heroes = {
    Home: normalizeHeroList(home.hero, canonicalById),
    Movies: normalizeHeroList(sectionHeroes.Movies, canonicalById),
    Shows: normalizeHeroList(sectionHeroes.Shows, canonicalById),
    Originals: normalizeHeroList(sectionHeroes.Originals, canonicalById),
  };

  return {
    sourceOfTruth: payload?.sourceOfTruth || (payload?.home ? 'watchvim-cms' : 'legacy-catalog'),
    frontendContractVersion: Number(payload?.frontendContractVersion || 0),
    publishedRevision: first(payload?.publishedRevision, payload?.publishedAt, payload?.updatedAt, ''),
    featuredIds: [...featuredIds],
    originalIds: [...originalIds],
    heroManagers: home.heroManagers || {},
    heroes,
  };
}

export function normalizeCatalog(payload) {
  const rawTitles = extractTitles(payload).map(normalizeTitle);
  const baseTitles = dedupeNormalized(rawTitles);
  const baseById = new Map(baseTitles.map((item) => [item.id, item]));

  const baseRails = extractRawRails(payload).map((rail, railIndex) => {
    const items = dedupeNormalized(rail.items.map((raw, itemIndex) => normalizeTitle(raw, railIndex * 1000 + itemIndex)))
      .map((item) => baseById.get(item.id) || item)
      .filter((item) => baseById.has(item.id));
    return { id: rail.id, title: rail.title, items };
  }).filter((rail) => rail.items.length);

  const cmsFeaturedIds = idSet(payload?.home?.featuredIds);
  const cmsOriginalIds = idSet(payload?.home?.watchvimOriginalIds);
  const railOriginalIds = idSet(baseRails.filter((rail) => /original/i.test(rail.title)).flatMap((rail) => rail.items.map((item) => item.id)));
  const shortIds = idSet(baseRails.filter((rail) => /\bshorts?\b|short films?/i.test(rail.title)).flatMap((rail) => rail.items.map((item) => item.id)));

  const titles = baseTitles.map((item) => ({
    ...item,
    featured: item.featured || cmsFeaturedIds.has(item.id),
    original: item.original || cmsOriginalIds.has(item.id) || railOriginalIds.has(item.id),
    short: item.short || shortIds.has(item.id),
  }));
  const canonicalById = new Map(titles.map((item) => [item.id, item]));
  const rails = baseRails.map((rail) => ({ ...rail, items: rail.items.map((item) => canonicalById.get(item.id)).filter(Boolean) }));

  const movies = titles.filter((item) => item.kind === 'movie' && !item.short);
  const shows = titles.filter((item) => item.kind === 'series' && !item.short);
  const originals = titles.filter((item) => item.original);
  const shorts = titles.filter((item) => item.short);
  const featuredFromCms = [...cmsFeaturedIds].map((id) => canonicalById.get(id)).filter(Boolean);
  const featuredFlags = titles.filter((item) => item.featured);
  const featured = featuredFromCms.length ? featuredFromCms : featuredFlags;
  const programming = normalizeProgramming(payload, canonicalById, cmsFeaturedIds, cmsOriginalIds);

  return {
    titles,
    rails,
    movies,
    shows,
    originals,
    shorts,
    featured: featured.length ? featured : (rails[0]?.items || titles.slice(0, 10)),
    programming,
  };
}

export async function fetchCatalog({ attempts = 3, signal } = {}) {
  let lastError;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      let catalogEndpoint = CATALOG_ENDPOINT;
      // A fresh manifest points to a content-addressed catalog. Old publishers
      // without a revision keep the existing fresh-catalog transport.
      const manifestResponse = await fetch('/api/watchvim-json?type=manifest', {
        headers: { accept: 'application/json' }, cache: 'no-store', signal,
      });
      if (manifestResponse.ok) {
        const manifest = await manifestResponse.json();
        const revision = String(manifest?.catalogRevision || '');
        if (/^[a-f0-9]{64}$/.test(revision)) catalogEndpoint += `&revision=${revision}`;
      }
      const versioned = catalogEndpoint !== CATALOG_ENDPOINT;
      const response = await fetch(catalogEndpoint, {
        method: 'GET',
        headers: { accept: 'application/json' },
        cache: versioned ? 'force-cache' : 'no-store',
        signal,
      });
      if (!response.ok) throw new Error(`Catalog request failed with ${response.status}`);
      return normalizeCatalog(await response.json());
    } catch (error) {
      if (signal?.aborted) throw error;
      lastError = error;
      if (attempt < attempts) await sleep(250 * attempt);
    }
  }
  throw lastError || new Error('Unable to load WatchVIM catalog');
}

